import {
  useEffect,
  useState,
} from 'react';


const API =
  '/api/contractor-onboarding/public/evidence-replacement';


function replacementToken() {
  const hash =
    window.location.hash || '';

  const params =
    new URLSearchParams(
      hash.startsWith('#')
        ? hash.slice(1)
        : hash
    );

  return params.get('token');
}


function documentLabel(value) {
  const labels = {
    security_licence:
      'Security licence',

    drivers_licence:
      'Driver licence',

    first_aid_certificate:
      'First Aid certificate',

    right_to_work:
      'Right-to-work evidence',

    other_supporting:
      'Supporting document',
  };

  return (
    labels[value]
    || value
    || 'Document'
  );
}


export default function ContractorEvidenceReplacement() {

  const [token] = useState(
    () => replacementToken()
  );

  const [status, setStatus] =
    useState('loading');

  const [request, setRequest] =
    useState(null);

  const [message, setMessage] =
    useState('');

  const [busy, setBusy] =
    useState(false);


  useEffect(() => {

    let cancelled = false;

    async function load() {

      if (!token) {
        setStatus('error');
        setMessage(
          'This replacement link is incomplete.'
        );
        return;
      }

      try {
        const response = await fetch(
          `${API}/resolve`,
          {
            headers: {
              'X-Replacement-Token':
                token,
            },
          }
        );

        let data = null;

        try {
          data = await response.json();
        } catch {
          data = null;
        }

        if (!response.ok) {
          throw new Error(
            data?.detail
            || 'Replacement request is unavailable.'
          );
        }

        if (!cancelled) {
          setRequest(data);
          setStatus('ready');
        }

      } catch (error) {
        if (!cancelled) {
          setStatus('error');
          setMessage(
            error?.message
            || 'Replacement request is unavailable.'
          );
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };

  }, [token]);


  async function upload(
    event
  ) {
    const file =
      event.target.files?.[0];

    if (!file) {
      return;
    }

    if (
      file.size
      > 10 * 1024 * 1024
    ) {
      setMessage(
        'Document exceeds the 10 MB upload limit.'
      );

      event.target.value = '';
      return;
    }

    setBusy(true);
    setMessage('');

    try {
      const response = await fetch(
        `${API}/upload`,
        {
          method: 'POST',

          headers: {
            'Content-Type':
              file.type
              || 'application/octet-stream',

            'X-Replacement-Token':
              token,

            'X-Filename':
              file.name,
          },

          body:
            file,
        }
      );

      let data = null;

      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(
          data?.detail
          || 'Replacement upload failed.'
        );
      }

      window.history.replaceState(
        null,
        '',
        window.location.pathname
      );

      setStatus('complete');

    } catch (error) {
      setMessage(
        error?.message
        || 'Replacement upload failed.'
      );
    } finally {
      setBusy(false);
      event.target.value = '';
    }
  }


  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0b1424',
        padding: '48px 20px',
        fontFamily:
          'Arial, Helvetica, sans-serif',
        color: '#111827',
      }}
    >
      <div
        style={{
          maxWidth: '760px',
          margin: '0 auto',
          background: '#ffffff',
          borderRadius: '18px',
          padding: '34px',
          boxShadow:
            '0 20px 45px rgba(0,0,0,.2)',
        }}
      >
        <div
          style={{
            color: '#64748b',
            fontWeight: 700,
            letterSpacing: '0.12em',
            fontSize: '13px',
          }}
        >
          CONTRACTOR ONBOARDING
        </div>

        <h1
          style={{
            margin: '8px 0 6px',
            fontSize: '28px',
          }}
        >
          Document replacement
        </h1>

        <p
          style={{
            marginTop: 0,
            color: '#64748b',
          }}
        >
          Secure contractor onboarding evidence replacement
        </p>


        {
          status === 'loading'
          && (
            <p>
              Checking replacement request…
            </p>
          )
        }


        {
          status === 'error'
          && (
            <div
              style={{
                marginTop: '24px',
                padding: '16px',
                borderRadius: '10px',
                background: '#fef2f2',
                color: '#b91c1c',
              }}
            >
              {message}
            </div>
          )
        }


        {
          status === 'ready'
          && request
          && (
            <>
              <div
                style={{
                  marginTop: '28px',
                  padding: '20px',
                  border:
                    '1px solid #e2e8f0',
                  borderRadius: '12px',
                }}
              >
                <div
                  style={{
                    fontSize: '13px',
                    color: '#64748b',
                    marginBottom: '5px',
                  }}
                >
                  Replacement required
                </div>

                <div
                  style={{
                    fontSize: '20px',
                    fontWeight: 700,
                  }}
                >
                  {
                    documentLabel(
                      request.document_type
                    )
                  }
                </div>

                {
                  request.rejected_filename
                  && (
                    <p
                      style={{
                        color: '#64748b',
                      }}
                    >
                      Rejected file:{' '}
                      {
                        request.rejected_filename
                      }
                    </p>
                  )
                }

                {
                  request.review_reason
                  && (
                    <div
                      style={{
                        marginTop: '16px',
                        padding: '14px',
                        background: '#fff7ed',
                        borderRadius: '8px',
                        color: '#9a3412',
                      }}
                    >
                      <strong>
                        Reviewer notes:
                      </strong>{' '}
                      {request.review_reason}
                    </div>
                  )
                }
              </div>

              <div
                style={{
                  marginTop: '24px',
                }}
              >
                <label
                  style={{
                    display: 'block',
                    fontWeight: 700,
                    marginBottom: '8px',
                  }}
                >
                  Upload replacement document
                </label>

                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                  disabled={busy}
                  onChange={upload}
                />

                <p
                  style={{
                    color: '#64748b',
                    fontSize: '13px',
                  }}
                >
                  PDF, JPG or PNG. Maximum 10 MB.
                </p>

                {
                  message
                  && (
                    <div
                      style={{
                        marginTop: '12px',
                        color: '#b91c1c',
                      }}
                    >
                      {message}
                    </div>
                  )
                }
              </div>
            </>
          )
        }


        {
          status === 'complete'
          && (
            <div
              style={{
                marginTop: '28px',
                padding: '24px',
                borderRadius: '12px',
                background: '#ecfdf5',
                color: '#065f46',
              }}
            >
              <h2
                style={{
                  marginTop: 0,
                }}
              >
                Replacement uploaded
              </h2>

              <p
                style={{
                  marginBottom: 0,
                }}
              >
                Your replacement document has been securely
                submitted to your contractor for administrative
                review. You may now close this window.
              </p>
            </div>
          )
        }

      </div>
    </div>
  );
}
